import { useAtomValue } from 'jotai';
import { InlineNotification } from '@carbon/react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';

import { DataTable } from 'components/data-table';
import { parseUTCdateTime } from 'utils/dates';
import { useCreateBlobArtifactsAtom, BlobArtifact } from './atoms';
import style from './blob-artifacts.module.scss';

type BlobArtifactRow = BlobArtifact & Record<string, unknown>;

const columnHelper = createColumnHelper<BlobArtifactRow>();

const columns = [
  columnHelper.accessor('evaluationOutputId', {
    id: 'evaluationOutputId',
    header: 'Output ID',
    size: 120,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor((row) => row.slotMonths.drained, {
    id: 'slotMonthsDrained',
    header: 'Slot Months',
    size: 120,
    cell: (info) => (
      <span className={info.getValue() ? style.drained : style.pending}>
        {info.getValue() ? 'Drained' : 'Pending'}
      </span>
    ),
  }),
  columnHelper.accessor((row) => row.slotMonths.blobPath, {
    id: 'slotMonthsBlobPath',
    header: 'Blob Path',
    size: 300,
    meta: { type: 'text' },
    cell: (info) => (
      <span className={style.blobPath}>{info.getValue() || '—'}</span>
    ),
  }),
  columnHelper.accessor((row) => row.slotMonths.rowCount, {
    id: 'slotMonthsRowCount',
    header: 'Row Count',
    size: 110,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor((row) => row.slotMonths.drainedAt, {
    id: 'slotMonthsDrainedAt',
    header: 'Drained At',
    size: 180,
    meta: { type: 'date' },
    cell: (info) => {
      const val = info.getValue();
      return val ? parseUTCdateTime(val, true) : '—';
    },
  }),
  columnHelper.accessor((row) => row.rawResponse.drained, {
    id: 'rawResponseDrained',
    header: 'Raw Response',
    size: 130,
    cell: (info) => (
      <span className={info.getValue() ? style.drained : style.pending}>
        {info.getValue() ? 'Drained' : 'Pending'}
      </span>
    ),
  }),
  columnHelper.accessor((row) => row.rawResponse.blobPath, {
    id: 'rawResponseBlobPath',
    header: 'Raw Blob Path',
    size: 300,
    meta: { type: 'text' },
    cell: (info) => (
      <span className={style.blobPath}>{info.getValue() || '—'}</span>
    ),
  }),
  columnHelper.accessor((row) => row.rawResponse.drainedAt, {
    id: 'rawResponseDrainedAt',
    header: 'Raw Drained At',
    size: 180,
    meta: { type: 'date' },
    cell: (info) => {
      const val = info.getValue();
      return val ? parseUTCdateTime(val, true) : '—';
    },
  }),
];

const tableConfig = {
  sorting: true,
  globalSearch: false,
  columnFilters: false,
  columnResizing: true,
  export: false as const,
};

const BlobArtifacts = ({ href }: { href: string }) => {
  const blobArtifactsAtom = useCreateBlobArtifactsAtom(href);
  const blobArtifacts = useAtomValue(blobArtifactsAtom);

  const data = blobArtifacts.data;

  if (data && 'error' in data)
    return <InlineNotification kind="error" title={data.error} lowContrast />;

  if (!data)
    return (
      <DataTable
        data={[] as BlobArtifactRow[]}
        columns={columns as ColumnDef<BlobArtifactRow>[]}
        getRowId={(row) => String(row.id)}
        isLoading={true}
        persistenceKey="evaluation-result-blob-artifacts"
        config={tableConfig}
      />
    );

  const artifacts = data as BlobArtifactRow[];

  if (artifacts.length === 0) {
    return (
      <div className={style.container}>
        <p>No blob artifacts found for this evaluation.</p>
        <p className={style.hint}>
          Blob artifacts are created when the drain job runs. Set{' '}
          <code>EVAL_OUTPUT_BLOB_DRAIN_ENABLED=true</code> to enable.
        </p>
      </div>
    );
  }

  return (
    <DataTable
      data={artifacts}
      columns={columns as ColumnDef<BlobArtifactRow>[]}
      getRowId={(row) => String(row.id)}
      persistenceKey="evaluation-result-blob-artifacts"
      config={tableConfig}
    />
  );
};

export { BlobArtifacts };
